import clsx from "clsx";
import React from "react";
import { RemoveScroll as ReactRemoveScroll } from "react-remove-scroll";
import * as styles from "./MainMenuPanel.module.scss";
import { Icon } from "@silversea-ssc/ui-components-react";
import { ValidatedPanelSection } from "../utils";
import PanelSection from "./PanelSection";
import { useSwipeBack } from "../useSwipeBack";
import { MainMenuSectionFragment } from "types/graphql-types";
import { isPresent } from "src/utils/checks";
import TrendingNow, { useTrendingNowProps } from "./TrendingNow";

type Props = {
  className: string;
  title: string;
  href?: string;
  trackingName: string | undefined;
  isExpanded: boolean;
  onClickBack: () => void;
  items: ValidatedPanelSection[];
  trendingNow: MainMenuSectionFragment["trendingNow"];
  id: string;
};

const MainMenuPanel: React.FC<Props> = ({
  className,
  title,
  href,
  trackingName,
  isExpanded,
  onClickBack,
  items,
  trendingNow,
  id
}) => {
  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipeBack(onClickBack);
  const filteredTrendingNow = trendingNow?.filter(isPresent) || [];
  const menuButtonId = `menu-${id}`;
  const trendingNowProps = useTrendingNowProps(filteredTrendingNow);
  const withTrendingNow = isPresent(trendingNowProps);

  const cssVarsStyle = {
    "--total-groups": items.length
  } as React.CSSProperties;

  return (
    <ReactRemoveScroll enabled={isExpanded} forwardProps>
      <div
        role="menu"
        aria-labelledby={menuButtonId}
        className={clsx(
          styles.subMenu,
          {
            [styles.subMenuExpanded]: isExpanded,
            [styles.withTrendingNow]: withTrendingNow
          },
          className
        )}
        style={cssVarsStyle}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className={styles.content}>
          <button
            type="button"
            id={menuButtonId}
            className={styles.backButton}
            role="menuitem"
            aria-haspopup="true"
            aria-expanded="false"
            onClick={onClickBack}
          >
            <Icon name="chevron-left" padding={false} size="1.5x" />
          </button>
          {href ? (
            <a
              className={styles.groupTitleBox}
              href={href}
              data-content-piece={trackingName}
            >
              {title}
              <Icon name="arrow-right" padding={false} size="1.5x" />
            </a>
          ) : (
            <div className={styles.groupTitleBox}>{title}</div>
          )}

          <ul className={styles.groups}>
            {items.map((subItem) => (
              <PanelSection key={subItem._key} {...subItem} />
            ))}
          </ul>
          {withTrendingNow && (
            <div className={styles.trendingNow}>
              <TrendingNow {...trendingNowProps} />
            </div>
          )}
        </div>
      </div>
    </ReactRemoveScroll>
  );
};

export default MainMenuPanel;
